import React, { useCallback, useEffect, useRef, useState } from 'react';
import { appViewportHeight } from '../../helpers/embeddedViewport';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: string;
  fullScreen?: boolean;
  dismissThreshold?: number;
}

export function BottomSheet({
  isOpen,
  onClose,
  children,
  height = appViewportHeight(0.75),
  fullScreen = false,
  dismissThreshold = 120,
}: BottomSheetProps) {
  const dragStartY = useRef<number | null>(null);
  const currentDragY = useRef(0);
  const [translateY, setTranslateY] = useState('100%');
  const [isDragging, setIsDragging] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setTranslateY('0%'));
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isOpen]);

  const dismiss = useCallback(() => {
    setTranslateY('100%');
    setTimeout(() => {
      setMounted(false);
      onClose();
    }, 320);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, dismiss]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    currentDragY.current = 0;
    setIsDragging(false);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta < 0) return;
    currentDragY.current = delta;
    setIsDragging(true);
    setTranslateY(`${delta}px`);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (currentDragY.current > dismissThreshold) {
      dismiss();
    } else {
      setIsDragging(false);
      setTranslateY('0%');
    }
    dragStartY.current = null;
  }, [dismiss, dismissThreshold]);

  if (!isOpen && !mounted) return null;

  /*
   * Os dois `position: fixed` abaixo são renderizados dentro do #root, que embedado vira bloco
   * de contenção dos `position: fixed` (ver helpers/embeddedViewport.ts). Por isso `top: 0` /
   * `bottom: 0` já resolvem contra a faixa visível — somar `--app-viewport-top` aplicaria a
   * correção duas vezes e colapsaria o backdrop. Pela mesma razão a altura cheia é `%`, e não
   * `vh`, que continua sendo a altura do iframe inteiro.
   */
  const sheetHeight = fullScreen ? '100%' : height;
  const borderRadius = fullScreen ? '0' : '20px 20px 0 0';

  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          // top/right/bottom/left em vez de `inset`: a shorthand não existe no
          // Chrome 86 (ambiente MV), deixando este backdrop sem cobrir a tela.
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 1299,
          backgroundColor: 'rgba(0,0,0,0.35)',
        }}
        onClick={dismiss}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          height: sheetHeight,
          maxHeight: '100%',
          borderRadius,
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          transform: `translateY(${translateY})`,
          transition: isDragging ? 'none' : 'transform 0.38s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          willChange: 'transform',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {!fullScreen && (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px', flexShrink: 0, cursor: 'grab' }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div style={{ width: 40, height: 4, borderRadius: 9999, backgroundColor: '#DEE2E6' }} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {children}
        </div>
      </div>
    </>
  );
}

export default BottomSheet;
